/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { IDistributionListsJoinRequest } from "Interfaces/DistributionLists/IDistributionListsJoinRequest";
import { formatDate } from "Utility/FormatHelper";

import { IDistributionListPendingRequestsProps } from "./IDistributionListPendingRequestsProps";

const DistributionListPendingRequestsList: React.FunctionComponent<
  IDistributionListPendingRequestsProps
> = (props) => {
  const {
    data,
    isLoadingDlJoinRequest,
    loadDlJoinRequestError,
    filteredCount,
    currentPage,
    pageSize,
    actions,
  } = props;

  const formatDisplayDate = (incomingDate: Date | undefined): React.ReactElement => {
    if (incomingDate) {
      return <span>{formatDate(incomingDate)}</span>;
    } else {
      return <></>;
    }
  };

  const columns: IColumn[] = [
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "First Name",
      fieldName: "firstName",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Last Name",
      fieldName: "lastName",
      minWidth: 160,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "emailAddress",
      name: "Email",
      ariaLabel: "Email",
      fieldName: "emailAddress",
      minWidth: 200,
      isResizable: true,
    },
    {
      key: "technologyAreas",
      name: "Technology Areas",
      ariaLabel: "Technology Areas",
      fieldName: "technologyAreas",
      minWidth: 300,
      isResizable: true,
    },
    {
      key: "distributionListName",
      name: "DL Name",
      ariaLabel: "DL Name",
      fieldName: "distributionListName",
      minWidth: 210,
    },
    {
      key: "dlTechnologyCategory",
      name: "DL Award Category",
      ariaLabel: "DL Award Category",
      fieldName: "distributionListTechCategory",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "dateRequested",
      name: "Date Requested",
      ariaLabel: "Date Requested",
      fieldName: "dateRequested",
      minWidth: 160,
      isResizable: true,
      onRender: (item: IDistributionListsJoinRequest): React.ReactElement => {
        return formatDisplayDate(item.dateRequested);
      },
    },
  ];

  if (isLoadingDlJoinRequest) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadDlJoinRequestError !== "") {
    return <div>{loadDlJoinRequestError}</div>;
  }

  const handleClick = (distributionListRequest: IDistributionListsJoinRequest): void => {
    actions.onRowClick(distributionListRequest);
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const selectedJoinRequest = props!.item as IDistributionListsJoinRequest;

    return defaultRender!({
      ...props,
      item: selectedJoinRequest,
      onClick: () => handleClick(selectedJoinRequest),
    });
  };

  return (
    <>
      <DetailsList
        items={data}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: IDistributionListsJoinRequest): string => `${item.requestId}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        onRenderRow={onRenderRow}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => actions.updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default DistributionListPendingRequestsList;
